import { Action, Dispatch } from 'redux';
import { createSelector } from 'reselect';

import { IAppState } from '../../../state/reducers';
import { selectTitleForNewAsset } from '../state/clipList';
import { selectSecurityProfileId } from '../../../state/securityProfiles';
import {
  IAsset, IDataHit, ISearchParams, searchAssets,
} from '../../../api/vault/searchEngine';
import { selectUserOrgsSecurityProfilesIds } from '../../../state/user/userOrgRole';

export enum ActionType {
  OpenDialog = 'CreateDownloadDialog.Open',
  CloseDialog = 'CreateDownloadDialog.Close',
  updateCreatedAssetField = 'CreateDownloadDialog.UpdateAssetField',
  ResetDialog = 'CreateDownloadDialog.Reset',
  SetWatermarkOptions = 'CreateDownloadDialog.SetWatermarkOptions',
  UpdateSelectedWatermark = 'CreateDownloadDialog.UpdateSelectedWatermark',
}

export interface ICreateDownloadDialogAction extends Action<ActionType> {
  payload?: any
}

export interface INewAsset {
  title: string;
  description: string;
  securityProfileId: string;
  watermarkAssetId: string;
}

export interface IState {
  isOpen: boolean;
  asset: INewAsset,
  savePostAction: () => void;
  saveMessage: string;
  watermarkOptions: IDataHit<IAsset>[];
  selectedWatermark: IDataHit<IAsset>;
}

const INITIAL_STATE: IState = {
  isOpen: false,
  asset: {
    title: '',
    description: '',
    securityProfileId: '',
    watermarkAssetId: '',
  },
  savePostAction: null,
  saveMessage: '',
  watermarkOptions: [],
  selectedWatermark: null,
};

export default function reducer(state: IState = INITIAL_STATE, action: ICreateDownloadDialogAction): IState {
  switch (action.type) {
    case ActionType.OpenDialog:
      return {
        ...state,
        isOpen: true,
        savePostAction: action.payload.callback,
        saveMessage: action.payload.message,
      };
    case ActionType.CloseDialog:
      return {
        ...state,
        asset: {
          ...state.asset,
          watermarkAssetId: '',
        },
        selectedWatermark: null,
        isOpen: false,
      };
    case ActionType.updateCreatedAssetField:
      return {
        ...state,
        asset: {
          ...state.asset,
          [action.payload.fieldName]: action.payload.value,
        },
      };
    case ActionType.ResetDialog:
      return {
        ...state,
        asset: INITIAL_STATE.asset,
        savePostAction: INITIAL_STATE.savePostAction,
        saveMessage: INITIAL_STATE.saveMessage,
      };
    case ActionType.SetWatermarkOptions:
      return {
        ...state,
        watermarkOptions: action.payload,
      };
    case ActionType.UpdateSelectedWatermark:
      return {
        ...state,
        asset: {
          ...state.asset,
          watermarkAssetId: action.payload ? action.payload._id : '',
        },
        selectedWatermark: action.payload,
      };
    default:
      return state;
  }
}

export const openCreateDownloadDialog = (postAction: { callback: () => void, message: string }) => (
  dispatch: Dispatch<ICreateDownloadDialogAction>,
  getState: () => IAppState,
) => {
  const state: IAppState = getState();

  dispatch({
    type: ActionType.updateCreatedAssetField,
    payload: {
      fieldName: 'title',
      value: selectTitleForNewAsset(state),
    },
  });

  dispatch({
    type: ActionType.updateCreatedAssetField,
    payload: {
      fieldName: 'securityProfileId',
      value: selectSecurityProfileId(state),
    },
  });

  dispatch({
    type: ActionType.OpenDialog,
    payload: postAction,
  });
};

export const closeCreateDownloadDialog = () => ({
  type: ActionType.CloseDialog,
});

export const updateCreatedAssetField = (fieldName: string, value: string) => ({
  type: ActionType.updateCreatedAssetField,
  payload: {
    fieldName,
    value,
  },
});

export const resetCreateDownloadDialog = () => ({
  type: ActionType.ResetDialog,
});

const selectSecurityProfileOptions = (state: IAppState) => state.securityProfiles?.securityProfileOptions ?? [];
const selectSelectedSecurityProfileId = (state: IAppState) => state.createDownloadDialog?.asset?.securityProfileId;

export const selectSelectedSecurityProfileOption = createSelector(
  selectSecurityProfileOptions,
  selectSelectedSecurityProfileId,
  (securityProfileOptions, selectedSecurityProfileId) => securityProfileOptions.find((profile: any) => profile._id === selectedSecurityProfileId),
);

export const isCreateDownloadOpen = () => (
  dispatch: Dispatch<ICreateDownloadDialogAction>,
  getState: () => IAppState,
) => getState().createDownloadDialog.isOpen;

export const getWatermarks = () => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const userOrgs = selectUserOrgsSecurityProfilesIds(getState());
  const searchParams: ISearchParams = {
    queryString: '',
    size: 250,
    page: 1,
    filters: [
      ['type', ['Image']],
      ['fileFormat', ['png']],
      ['owner', userOrgs],
    ],
  };

  const response = await searchAssets(searchParams);
  const watermarks = response.data.data.hits.hits;

  dispatch({
    type: ActionType.SetWatermarkOptions,
    payload: watermarks,
  });
};

export const updateSelectedWatermark = (watermark: IDataHit<IAsset>) => ({
  type: ActionType.UpdateSelectedWatermark,
  payload: watermark,
});
